import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';
import toast from 'react-hot-toast';
import PageHero from '../components/Common/PageHero';
import MetricCard from '../components/Common/MetricCard';
import DataGrid from '../components/Common/DataGrid';

interface LoginAudit {
  id: number;
  userId: number;
  email: string;
  action: string;
  ipAddress?: string;
  userAgent?: string;
  metadata?: Record<string, any>;
  createdAt: string;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

const ACTION_OPTIONS = [
  { value: '', label: 'All actions' },
  { value: 'otp_sent', label: 'OTP sent' },
  { value: 'otp_verified', label: 'OTP verified' },
  { value: 'otp_failed', label: 'OTP failed' },
  { value: 'login_success', label: 'Login success' },
  { value: 'login_failed', label: 'Login failed' },
  { value: 'logout', label: 'Logout' },
  { value: 'password_reset_requested', label: 'Password reset requested' },
  { value: 'password_reset_completed', label: 'Password reset completed' },
  { value: 'account_locked', label: 'Account locked' },
];

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const getActionBadge = (action: string) => {
  const styles: Record<string, string> = {
    otp_sent: 'bg-green-100 text-green-700',
    otp_verified: 'bg-blue-100 text-blue-700',
    otp_failed: 'bg-red-100 text-red-700',
    login_success: 'bg-emerald-100 text-emerald-700',
    login_failed: 'bg-rose-100 text-rose-700',
    logout: 'bg-gray-100 text-gray-700',
    password_reset_requested: 'bg-amber-100 text-amber-700',
    password_reset_completed: 'bg-teal-100 text-teal-700',
    account_locked: 'bg-orange-100 text-orange-700',
  };
  return styles[action] || 'bg-gray-100 text-gray-700';
};

const LoginAudits: React.FC = () => {
  const navigate = useNavigate();
  const [audits, setAudits] = useState<LoginAudit[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  });
  const [actionFilter, setActionFilter] = useState('');
  const [emailFilter, setEmailFilter] = useState('');

  const fetchAudits = async (page = 1) => {
    try {
      setLoading(true);
      const response = await authAPI.getLoginAudits({
        page,
        limit: 50,
        action: actionFilter || undefined,
        email: emailFilter || undefined,
      });
      setAudits(response.data || []);
      setPagination(response.pagination);
    } catch (error) {
      console.error('Error fetching login audits:', error);
      toast.error('Failed to load login audits');
      setAudits([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAudits(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionFilter, emailFilter]);

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > pagination.totalPages) return;
    fetchAudits(newPage);
  };

  const otpSentCount = audits.filter((a) => a.action === 'otp_sent').length;
  const otpVerifiedCount = audits.filter((a) => a.action === 'otp_verified').length;
  const otpFailedCount = audits.filter((a) => a.action === 'otp_failed').length;

  return (
    <div className="space-y-8">
      <PageHero
        badge={{ label: 'Security and access', tone: 'red' }}
        title="Login Audit Trail"
        description="Track OTP requests, login attempts, password resets, and account activity across the platform."
        stats={[
          { icon: 'fas fa-list-alt', label: `${pagination.total} events` },
        ]}
        actions={
          <button
            onClick={() => navigate('/users')}
            className="rounded-2xl bg-white px-6 py-3 font-semibold text-slate-900 hover:bg-slate-100"
          >
            Back to users
          </button>
        }
      />

      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        <MetricCard label="OTP sent" value={otpSentCount} hint="WhatsApp OTP requests" icon="fas fa-paper-plane" accent="green" />
        <MetricCard label="OTP verified" value={otpVerifiedCount} hint="Successful OTP verifications" icon="fas fa-check-circle" accent="blue" />
        <MetricCard label="OTP failed" value={otpFailedCount} hint="Failed OTP attempts on page" icon="fas fa-exclamation-triangle" accent="red" />
      </div>

      <DataGrid
        title="Login events"
        description={`${audits.length} of ${pagination.total} events`}
        loading={loading}
        emptyText="No login audit events found."
        rows={audits}
        toolbar={
          <div className="flex flex-col gap-3 sm:flex-row">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <i className="fas fa-filter text-gray-400"></i>
              </div>
              <select
                value={actionFilter}
                onChange={(e) => setActionFilter(e.target.value)}
                className="pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-xl text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/40 min-w-[180px]"
              >
                {ACTION_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="relative flex-1 max-w-md">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <i className="fas fa-search text-gray-400"></i>
              </div>
              <input
                type="text"
                value={emailFilter}
                onChange={(e) => setEmailFilter(e.target.value)}
                placeholder="Filter by email..."
                className="w-full pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-xl text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
              />
            </div>
          </div>
        }
        columns={[
          {
            key: 'timestamp',
            header: 'Timestamp',
            className: 'whitespace-nowrap',
            render: (entry) => <span className="text-xs text-gray-600">{formatDate(entry.createdAt)}</span>,
          },
          {
            key: 'email',
            header: 'User',
            render: (entry) => (
              <div>
                <div className="text-sm font-medium text-gray-900">{entry.email}</div>
                <div className="text-xs text-gray-500">ID: {entry.userId}</div>
              </div>
            ),
          },
          {
            key: 'action',
            header: 'Action',
            render: (entry) => (
              <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold ${getActionBadge(entry.action)}`}>
                {entry.action.replace(/_/g, ' ')}
              </span>
            ),
          },
          {
            key: 'ip',
            header: 'IP Address',
            render: (entry) => <span className="text-xs text-gray-600">{entry.ipAddress || '-'}</span>,
          },
          {
            key: 'details',
            header: 'Details',
            render: (entry) => {
              const details: string[] = [];
              if (entry.metadata?.phone) details.push(`Phone: ${entry.metadata.phone}`);
              if (entry.metadata?.reason) details.push(`Reason: ${entry.metadata.reason}`);
              if (entry.metadata?.method) details.push(`Method: ${entry.metadata.method}`);
              if (entry.metadata?.resend) details.push('Resend: yes');
              return (
                <div className="text-xs text-gray-600">
                  {details.length > 0 ? details.join(' • ') : '-'}
                </div>
              );
            },
          },
        ]}
      />

      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
          <button
            onClick={() => handlePageChange(pagination.page - 1)}
            disabled={!pagination.hasPrev}
            className="px-4 py-2 text-sm font-semibold text-gray-700 bg-gray-50 rounded-xl hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i className="fas fa-chevron-left mr-2"></i>
            Previous
          </button>
          <span className="text-sm text-gray-600">
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <button
            onClick={() => handlePageChange(pagination.page + 1)}
            disabled={!pagination.hasNext}
            className="px-4 py-2 text-sm font-semibold text-gray-700 bg-gray-50 rounded-xl hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
            <i className="fas fa-chevron-right ml-2"></i>
          </button>
        </div>
      )}
    </div>
  );
};

export default LoginAudits;
